import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    console.log('[Vivendius] Logout - Redirecting to /login');
    logout();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false); // Close menu after navigation on mobile
  };

  const adminMenuItems = [
    { path: '/dashboard', icon: '📊', label: t('admin.dashboard'), testId: 'nav-dashboard' },
    { path: '/admin/waitlist', icon: '🎫', label: t('sidebar.waitlist'), testId: 'nav-admin-waitlist' },
    { path: '/admin/lenders', icon: '👥', label: t('admin.lenders'), testId: 'nav-admin-lenders' },
    { path: '/admin/loans', icon: '💰', label: t('admin.loans'), testId: 'nav-admin-loans' },
    { path: '/admin/loan-requests', icon: '📋', label: t('sidebar.loanRequests'), testId: 'nav-loan-requests' },
    { path: '/admin/cancellations', icon: '🔄', label: t('sidebar.pendingCancellations'), testId: 'nav-cancellations' },
    { path: '/admin/cancellations/approved', icon: '✅', label: t('sidebar.approvedCancellations'), testId: 'nav-approved-cancellations' },
    { path: '/admin/config/rate-tiers', icon: '⚙️', label: t('sidebar.rateTiers'), testId: 'nav-rate-config' },
    { path: '/admin/landing-settings', icon: '🎬', label: i18n.language === 'ca' ? 'Landing / Vídeos' : 'Landing / Vídeos', testId: 'nav-landing-settings' },
    { path: '/admin/cdn-status', icon: '☁️', label: 'CDN', testId: 'nav-cdn-status' },
    { path: '/profile', icon: '👤', label: t('sidebar.profile'), testId: 'nav-profile' },
    { path: 'https://www.vivendius.cat/', icon: '🏠', label: t('sidebar.projects'), testId: 'nav-projects', external: true },
  ];

  const lenderMenuItems = [
    { path: '/dashboard', icon: '📊', label: t('sidebar.dashboard'), testId: 'nav-dashboard' },
    { path: '/loans', icon: '💼', label: t('loans.title'), testId: 'nav-loans' },
    { path: '/contracts', icon: '📄', label: t('contracts.title'), testId: 'nav-contracts' },
    { path: '/invitations', icon: '🎁', label: t('invitations.myInvitations'), testId: 'nav-invitations' },
    { path: '/profile', icon: '👤', label: t('sidebar.profile'), testId: 'nav-profile' },
    { path: 'https://www.vivendius.cat/', icon: '🏠', label: t('sidebar.projects'), testId: 'nav-projects', external: true },
  ];

  const menuItems = user?.role === 'admin' ? adminMenuItems : lenderMenuItems;

  return (
    <>
      {/* Mobile Menu Button (Hamburger) */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        style={{
          position: 'fixed',
          top: '1rem',
          left: '1rem',
          zIndex: 2000,
          background: 'linear-gradient(135deg, #103766 0%, #1a4d7a 100%)',
          border: 'none',
          borderRadius: '8px',
          padding: '0.75rem',
          cursor: 'pointer',
          display: 'none', // Hidden by default, shown on mobile via media query
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
        className="mobile-menu-button"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '24px' }}>
          <div style={{ width: '100%', height: '3px', background: 'white', borderRadius: '2px', transition: 'all 0.3s' }}></div>
          <div style={{ width: '100%', height: '3px', background: 'white', borderRadius: '2px', transition: 'all 0.3s' }}></div>
          <div style={{ width: '100%', height: '3px', background: 'white', borderRadius: '2px', transition: 'all 0.3s' }}></div>
        </div>
      </button>

      {/* Backdrop for mobile */}
      {isMobileMenuOpen && (
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.6)',
            zIndex: 1500,
            display: 'none'
          }}
          className="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <div 
        className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}
        style={{
          width: '280px',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          background: 'linear-gradient(180deg, #103766 0%, #1a4d7a 100%)',
          boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1600,
          transition: 'transform 0.3s ease'
        }}
      >
      {/* Logo Section - Reduced for admin */}
      <div style={{
        padding: user?.role === 'admin' ? '1.25rem 1.5rem' : '2rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <img 
          src="/assets/vivendius-logo-white.png" 
          alt="Vivendius" 
          style={{ 
            width: '100%', 
            maxWidth: user?.role === 'admin' ? '160px' : '200px', 
            margin: '0 auto', 
            display: 'block' 
          }}
        />
        <p style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: user?.role === 'admin' ? '0.65rem' : '0.75rem',
          textAlign: 'center',
          marginTop: '0.35rem'
        }}>Investments Made Easy, S.L.</p>
      </div>

      {/* User Info - Compact for admin */}
      <div style={{
        padding: user?.role === 'admin' ? '1rem 1.5rem' : '1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: user?.role === 'admin' ? '0.75rem' : '1rem',
          borderRadius: '12px'
        }}>
          <p style={{ 
            color: 'white', 
            fontWeight: '600', 
            fontSize: user?.role === 'admin' ? '0.85rem' : '0.95rem', 
            wordBreak: 'break-word' 
          }}>
            {user?.name} {user?.surname}
          </p>
          <p style={{ 
            color: 'rgba(255,255,255,0.7)', 
            fontSize: user?.role === 'admin' ? '0.7rem' : '0.8rem', 
            marginTop: '0.25rem' 
          }}>
            {user?.role === 'admin' ? '👑 ' + (t('profile.administrator') || 'Administrador') : '💎 ' + (t('profile.lender') || 'Prestamista')}
          </p>
        </div>
      </div>

      {/* Navigation Menu - Compact for admin */}
      <nav style={{ 
        flex: 1, 
        padding: user?.role === 'admin' ? '0.75rem 0' : '1.5rem 0', 
        overflowY: 'auto' 
      }}>
        {menuItems.map((item) => (
          item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={item.testId}
              style={{
                width: '100%',
                padding: user?.role === 'admin' ? '0.6rem 1.5rem' : '0.875rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: user?.role === 'admin' ? '0.75rem' : '1rem',
                background: 'transparent',
                color: 'white',
                fontSize: user?.role === 'admin' ? '0.85rem' : '0.95rem',
                fontWeight: '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderLeft: '4px solid transparent',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ fontSize: user?.role === 'admin' ? '1.25rem' : '1.5rem' }}>{item.icon}</span>
              <span>{item.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.6 }}>↗</span>
            </a>
          ) : (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              data-testid={item.testId}
              style={{
                width: '100%',
                padding: user?.role === 'admin' ? '0.6rem 1.5rem' : '0.875rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: user?.role === 'admin' ? '0.75rem' : '1rem',
                border: 'none',
                background: isActive(item.path) 
                  ? 'rgba(255,255,255,0.15)' 
                  : 'transparent',
                color: 'white',
                fontSize: user?.role === 'admin' ? '0.85rem' : '0.95rem',
                fontWeight: isActive(item.path) ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderLeft: isActive(item.path) ? '4px solid #FF9800' : '4px solid transparent',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: user?.role === 'admin' ? '1.25rem' : '1.5rem', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
            </button>
          )
        ))}
      </nav>

      {/* Logout Button - Compact for admin */}
      <div style={{ 
        padding: user?.role === 'admin' ? '1rem 1.5rem' : '1.5rem', 
        borderTop: '1px solid rgba(255,255,255,0.1)' 
      }}>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          style={{
            width: '100%',
            padding: user?.role === 'admin' ? '0.7rem' : '0.875rem',
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: '8px',
            color: 'white',
            fontSize: user?.role === 'admin' ? '0.85rem' : '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
          }}
        >
          <span>🚪</span>
          <span>{t('common.logout') || 'Cerrar Sesión'}</span>
        </button>
      </div>
    </div>
    </>
  );
}

export default Sidebar;